import { useState } from 'react';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import type { DistrictMapPmtiles } from '@citizen-walk/shared-types';
import { storage } from '../../lib/firebase';
import { useT } from '../../i18n';
import {
  fetchPmtilesBlobWithProgress,
  putOfflineMapEntry,
  type OfflineMapEntry,
} from './offlineMap';
import { useAutoMapPrompt } from './useAutoMapPrompt';

/**
 * `district.mapDownloadPolicy === 'auto'` 地区の初回起動時に PMTiles 自動 DL を促す dialog
 * (Sprint BG-DataSettings 19、U-C13、旧 `old/apps/user/src/components/AutoMapDialog.tsx` 159 行踏襲)。
 *
 * 踏襲した点 (旧 → v2):
 *  - 表示条件 (mapDownloadPolicy 'auto' + cache miss + 未永続 dismiss) は `useAutoMapPrompt` に切出
 *  - layout (h2 title + intro + size + wifi-only checkbox + 3 button: 保存 / あとで / 今後表示しない)
 *  - Wi-Fi 判定: `navigator.connection.type ?? effectiveType` で 'wifi' / '4g' 以外なら警告
 *    (iOS Safari は `connection` 未対応 → conn === null なら素通し)
 *  - DL 進捗 (loaded/total MB) を inline 表示、busy 中は全 button disabled
 *  - 永続 dismiss は localStorage 書込 (key は v2 命名規約 `cw.autoMap.prompted.{districtId}`)
 *
 * 変えた点 (細部):
 *  - 旧 `downloadOfflineMap(district, onProgress)` 関数は v2 に未移植 (MapDownloadPage が直接
 *    `getDownloadURL` + `fetchPmtilesBlobWithProgress` + `putOfflineMapEntry` の 3 段組で扱う方針)
 *    なので、本 component でも同 3 段を inline で実装 (重複コードは Sprint 21 で `downloadOfflineMap`
 *    helper として抽出予定、U-C13 完了時点では minimum)。
 *  - 旧 `district.mapPmtiles!` 非 null assertion は型安全に `useAutoMapPrompt.meta` 経由で受領。
 *  - i18n: 旧 `useTranslation('user')` → v2 `useT()` (shared-i18n)。namespace は無視で
 *    key prefix `offline_map.auto_dialog.*` に統一。
 *  - 旧 inline 「📍 / 📥 / DL中…」絵文字を踏襲、wording は完全 i18n 化。
 *  - `districtId` / `districtName` を props で受領 (旧は `useDistrict()` 直 inline、v2 で
 *    UI test しやすさのため props 化)。
 *
 * 廃止した点:
 *  - 旧 `getConnectionTypeOrNull()` 自由関数 → 本 component の private helper として inline。
 *  - 旧 `useDistrict()` 直接呼出 → AppHome 側から props で districtId / districtName を渡す。
 */

function getConnectionTypeOrNull(): string | null {
  if (typeof navigator === 'undefined') return null;
  const conn = (
    navigator as Navigator & {
      connection?: { type?: string; effectiveType?: string };
    }
  ).connection;
  return conn?.type ?? conn?.effectiveType ?? null;
}

export interface AutoMapDialogProps {
  /** 対象地区 ID (cache 判定 key)。AppHome/MapPage の readiness gate 通過後の解決済 district.id。 */
  districtId: string;
  /** title 表示用 (i18n `{districtName}` placeholder)。 */
  districtName: string;
}

export function AutoMapDialog({ districtId, districtName }: AutoMapDialogProps) {
  const t = useT();
  const prompt = useAutoMapPrompt(districtId);
  const [wifiOnly, setWifiOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!prompt.visible || !prompt.meta) return null;
  const meta: DistrictMapPmtiles = prompt.meta;
  const sizeMb = (meta.sizeBytes / 1024 / 1024).toFixed(1);

  async function handleConfirm() {
    if (wifiOnly) {
      const conn = getConnectionTypeOrNull();
      // navigator.connection が取れる Android Chrome のみ wifi 判定。iOS は不明なので素通し (旧踏襲)。
      if (conn !== null && conn !== 'wifi' && conn !== '4g') {
        setError(t('offline_map.auto_dialog.error_wifi_required'));
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      const url = await getDownloadURL(storageRef(storage, meta.storagePath));
      const blob = await fetchPmtilesBlobWithProgress(url, (loaded, total) => {
        setProgress({ loaded, total: total || meta.sizeBytes });
      });
      const entry: OfflineMapEntry = {
        storagePath: meta.storagePath,
        schema: meta.schema ?? 'protomaps',
        blob,
        sizeBytes: blob.size,
        maxZoom: meta.maxZoom,
        generatedAt: meta.generatedAt,
        downloadedAt: new Date().toISOString(),
      };
      await putOfflineMapEntry(districtId, entry);
      prompt.close();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auto-map-dialog-title"
      data-testid="auto-map-dialog"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-6 max-h-[90vh] overflow-auto">
        <h2 id="auto-map-dialog-title" className="text-xl font-bold mb-3">
          📍 {t('offline_map.auto_dialog.title', { districtName })}
        </h2>
        <p className="mb-3 text-slate-700">{t('offline_map.auto_dialog.intro')}</p>
        <p className="text-sm text-slate-700 mb-4">
          {t('offline_map.auto_dialog.size_label')}: 約 {sizeMb} MB
        </p>
        <label className="flex items-center gap-2 mb-4 text-sm">
          <input
            type="checkbox"
            checked={wifiOnly}
            onChange={(e) => setWifiOnly(e.target.checked)}
            className="w-5 h-5"
            data-testid="auto-map-dialog-wifi-only"
          />
          <span>{t('offline_map.auto_dialog.wifi_only')}</span>
        </label>
        {error && (
          <p className="text-danger-700 text-sm mb-3" data-testid="auto-map-dialog-error">
            {error}
          </p>
        )}
        {busy && progress ? (
          <p className="text-sm text-slate-600 mb-3" data-testid="auto-map-dialog-progress">
            {t('offline_map.auto_dialog.busy_progress', {
              loaded: (progress.loaded / 1024 / 1024).toFixed(1),
              total: (progress.total / 1024 / 1024).toFixed(1),
            })}
          </p>
        ) : null}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={busy}
          className="w-full bg-primary-700 text-white py-3 rounded-md font-medium mb-2 disabled:opacity-40"
          data-testid="auto-map-dialog-confirm"
        >
          {busy
            ? t('offline_map.auto_dialog.busy_saving')
            : `📥 ${t('offline_map.auto_dialog.confirm')}`}
        </button>
        <button
          type="button"
          onClick={prompt.close}
          disabled={busy}
          className="w-full text-slate-700 py-3 disabled:opacity-40"
          data-testid="auto-map-dialog-dismiss"
        >
          {t('offline_map.auto_dialog.dismiss')}
        </button>
        <button
          type="button"
          onClick={prompt.dismissForever}
          disabled={busy}
          className="w-full text-slate-500 py-2 text-sm underline disabled:opacity-40"
          data-testid="auto-map-dialog-dont-ask"
        >
          {t('offline_map.auto_dialog.dont_ask_again')}
        </button>
      </div>
    </div>
  );
}
